import Hls from 'hls.js'
import type { ComponentPublicInstance } from 'vue'

export interface EventVideoSources {
  source: string
  sourceStream?: string
}

const cleanups = new WeakMap<HTMLVideoElement, () => void>()

const attachVideoSource = (
  video: HTMLVideoElement,
  { source, sourceStream }: EventVideoSources
): (() => void) => {
  const cleanup = () => {
    video.removeAttribute('src')
    video.load()
  }

  // Prefer direct file — native decode is far cheaper than HLS.js
  if (source) {
    video.src = source
    return cleanup
  }

  if (!sourceStream) {
    return cleanup
  }

  if (!Hls.isSupported()) {
    video.src = sourceStream
    return cleanup
  }

  const hls = new Hls({
    enableWorker: true,
    maxBufferLength: 10,
    maxMaxBufferLength: 20,
  })
  hls.loadSource(sourceStream)
  hls.attachMedia(video)
  hls.on(Hls.Events.ERROR, (_event, data) => {
    if (data.fatal) {
      hls.destroy()
      video.src = sourceStream
    }
  })

  return () => {
    hls.destroy()
    cleanup()
  }
}

/** Binds a source once per element until unbind is called. */
export const bindEventVideoSource = (
  video: HTMLVideoElement,
  sources: EventVideoSources
) => {
  if (cleanups.has(video)) return
  cleanups.set(video, attachVideoSource(video, sources))
}

export const unbindEventVideoSource = (video: HTMLVideoElement | null) => {
  if (!video) return
  cleanups.get(video)?.()
  cleanups.delete(video)
}

export const useEventVideoHlsBinding = () => {
  const bindVideoElement = (
    el: Element | ComponentPublicInstance | null,
    sources: EventVideoSources
  ) => {
    if (!(el instanceof HTMLVideoElement)) return
    bindEventVideoSource(el, sources)
  }

  const unbindVideoElement = (el: HTMLVideoElement | null) => {
    unbindEventVideoSource(el)
  }

  return { bindVideoElement, unbindVideoElement }
}
